import {
  IInboxConversationDetailResponse,
  IInboxConversationNote,
  IInboxConversationResponse,
  IInboxConversationsResponse,
  IInboxConversationUnreadCountResponse,
  InboxConversationChannel,
  InboxConversationStatus,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { CacheTags } from '../../types/cacheTags';
import { emptySplitApi } from '../baseApi';

interface ConversationArgs {
  organisationId: string;
  conversationId: string;
}

const LIST = { type: CacheTags.InboxConversations, id: `LIST` } as const;

export const inboxConversationApi = emptySplitApi.injectEndpoints({
  endpoints: builder => {
    // Every action returns the conversation and changes what the list, the detail and the unread badge show
    const action = <Body extends object = object>(method: `POST` | `PUT`, path: string) =>
      builder.mutation<IInboxConversationResponse, ConversationArgs & Body>({
        query: ({ organisationId, conversationId, ...data }) => ({
          url: `conversations/${conversationId}/${path}`,
          method,
          headers: {
            [orgHeader]: organisationId,
          },
          data,
        }),
        invalidatesTags: (_result, _error, args) => [LIST, { type: CacheTags.InboxConversations, id: args.conversationId }],
      });

    return {
      getInboxConversations: builder.query<IInboxConversationsResponse, {
        organisationId: string;
        statuses: InboxConversationStatus[];
        channel?: InboxConversationChannel;
        assignee?: string;
        unmatched?: boolean;
        search?: string;
        before?: string;
      }>({
        query: ({ organisationId, ...params }) => ({
          url: `conversations`,
          method: `GET`,
          headers: {
            [orgHeader]: organisationId,
          },
          params,
        }),
        providesTags: [LIST],
      }),
      getInboxUnreadCount: builder.query<IInboxConversationUnreadCountResponse, { organisationId: string }>({
        query: ({ organisationId }) => ({
          url: `conversations/unread-count`,
          method: `GET`,
          headers: {
            [orgHeader]: organisationId,
          },
        }),
        providesTags: [LIST],
      }),
      getInboxConversation: builder.query<IInboxConversationDetailResponse, ConversationArgs>({
        query: ({ organisationId, conversationId }) => ({
          url: `conversations/${conversationId}`,
          method: `GET`,
          headers: {
            [orgHeader]: organisationId,
          },
        }),
        providesTags: (_result, _error, args) => [
          { type: CacheTags.InboxConversations, id: args.conversationId },
          // A reply sent with the Comms tab composers shows up in the transcript
          { type: CacheTags.Communications, id: `LIST` },
        ],
      }),
      markInboxConversationRead: action(`POST`, `read`),
      resolveInboxConversation: action(`POST`, `resolve`),
      archiveInboxConversation: action(`POST`, `archive`),
      reopenInboxConversation: action(`POST`, `reopen`),
      assignInboxConversation: action<{ userId: string | null }>(`PUT`, `assignee`),
      assignInboxConversationContact: action<{ contactId: number }>(`PUT`, `contact`),
      addInboxConversationNote: builder.mutation<{ note: IInboxConversationNote }, ConversationArgs & {
        content: string;
        mentionedUsers: string[];
      }>({
        query: ({ organisationId, conversationId, ...data }) => ({
          url: `conversations/${conversationId}/notes`,
          method: `POST`,
          headers: {
            [orgHeader]: organisationId,
          },
          data,
        }),
        invalidatesTags: (_result, _error, args) => [{ type: CacheTags.InboxConversations, id: args.conversationId }],
      }),
    };
  },
});

export const {
  useGetInboxConversationsQuery,
  useGetInboxUnreadCountQuery,
  useGetInboxConversationQuery,
  useMarkInboxConversationReadMutation,
  useResolveInboxConversationMutation,
  useArchiveInboxConversationMutation,
  useReopenInboxConversationMutation,
  useAssignInboxConversationMutation,
  useAssignInboxConversationContactMutation,
  useAddInboxConversationNoteMutation,
} = inboxConversationApi;
